import type { AssignedPhotoMeta, AssignedPhotoPayload } from '@lite-workspace/dto-fotoclass-templates';
import type { HexColor } from '../../interfaces/color.type';
import type { ImageAlbum } from '../../interfaces/image-album.interface';

const SYNTHESIZED_DOMINANT_COLOR: HexColor = '#ffffff';

/**
 * `AssignedPhotoPayload` ↔ `ImageAlbum` — the ONE conversion pair for a persisted photo. The image-box
 * codec routes decode/encode through it, and `@lite-workspace/shared/template-document`'s photo ops call
 * it directly (their placement math runs on `ImageAlbum`); a second mapping would drift field-by-field.
 */
export function imageAlbumFromAssignedPhoto(payload: AssignedPhotoPayload, onWarn?: (message: string) => void): ImageAlbum {
  const meta = payload.meta;
  // Visually-significant fallback, not a neutral placeholder (ownerDecisions #7) — == null so a persisted null warns too.
  if (meta?.dominantColor == null) {
    onWarn?.(`params.assignedPhoto.meta.dominantColor missing, synthesizing "${SYNTHESIZED_DOMINANT_COLOR}"`);
  }

  return {
    id: payload.photoId,
    image: payload.url,
    // Каждое поле — ровно своя версия, без подстановки соседней. `versions` — авторский путь PSD-шаблона,
    // `meta.renditions` — бэг персист-нормализатора: адреса, которым в `versions` не хватает width/height/fileId.
    // Раньше отсутствующие large и thumb подменялись оригиналом, и лестница рендера принимала
    // 30-мегапиксельный исходник за 1920-й файл. Оригинал остаётся доступен через `image`.
    big_1920: payload.versions?.large?.path ?? meta?.renditions?.big_1920,
    medium_1280: payload.versions?.medium?.path ?? meta?.renditions?.medium_1280,
    small: meta?.renditions?.small,
    thumb: payload.versions?.thumb?.path ?? meta?.renditions?.thumb,
    width: payload.nativeWidth,
    height: payload.nativeHeight,
    // AssignedPhotoMeta.dominantColor is a plain string on the wire; ImageAlbum requires the HexColor brand.
    image_dominant_color: (meta?.dominantColor as HexColor | undefined) ?? SYNTHESIZED_DOMINANT_COLOR,
    filename: meta?.filename ?? '',
    thumb_hash: meta?.thumbHash,
    personId: meta?.personId,
    tags: meta?.tags,
    faces: meta?.faces,
    exif_data: meta?.exifDate !== undefined ? { DateTimeOriginal: meta.exifDate } : undefined,
    personBboxes: payload.personBboxes,
  };
}

function encodeAssignedPhotoMeta(image: ImageAlbum): AssignedPhotoMeta {
  const meta: { -readonly [K in keyof AssignedPhotoMeta]?: AssignedPhotoMeta[K] } = {
    filename: image.filename,
    dominantColor: image.image_dominant_color,
  };
  if (image.exif_data?.DateTimeOriginal !== undefined) meta.exifDate = image.exif_data.DateTimeOriginal;
  if (image.thumb_hash !== undefined) meta.thumbHash = image.thumb_hash;
  if (image.personId !== undefined) meta.personId = image.personId;
  if (image.tags !== undefined) meta.tags = image.tags;
  if (image.faces !== undefined) meta.faces = image.faces;

  const renditions = {
    ...(image.thumb !== undefined ? { thumb: image.thumb } : {}),
    ...(image.small !== undefined ? { small: image.small } : {}),
    ...(image.medium_1280 !== undefined ? { medium_1280: image.medium_1280 } : {}),
    ...(image.big_1920 !== undefined ? { big_1920: image.big_1920 } : {}),
  };
  // Пустой бэг не пишем: `meta.renditions` должен остаться отсутствующим, а не стать `{}`.
  if (Object.keys(renditions).length > 0) meta.renditions = renditions;

  return meta;
}

// Раскладка версий по полям утверждена: large -> big_1920, medium -> medium_1280, thumb -> thumb, без
// подстановок (decode выше). Обратно `versions` не собираются: ObjectAssetVersions требует на каждый тир
// ещё width/height/fileId, а отдельного ключа под 640 у него нет вовсе — выдумывать эти числа значит снова
// соврать о размере. Сами АДРЕСА при этом не теряются: они уезжают в `meta.renditions` и оттуда же читаются.
export function assignedPhotoFromImageAlbum(image: ImageAlbum): AssignedPhotoPayload {
  return {
    photoId: image.id,
    url: image.image,
    nativeWidth: image.width,
    nativeHeight: image.height,
    personBboxes: image.personBboxes,
    meta: encodeAssignedPhotoMeta(image),
  };
}
